import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../contexts/AuthContext';
import { tasksAPI } from '../services/api';

import '../styles/FulltaskModal.css';

const TaskModal = ({ isOpen, onClose, task = null }) => {
  const { createTask, updateTask, users, projects, loadTasks } = useTask();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    urgency: 3,
    deadline: '',
    status: 'todo',
    assignedTo: '',
    assignees: [],
    subtasks: [],
  });
  const [newSubtask, setNewSubtask] = useState('');

  const [reviews, setReviews] = useState([]);

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [feedbackText, setFeedbackText] = useState('');

  useEffect(() => {
    if (task) {
      // Handle both assignedTo and assignee fields
      const assignedUserId = task.assignedTo || task.assignee?._id || task.assignee || '';
      const assignedUserIds = task.assignees ? task.assignees.map(u => u._id || u) : [];

      setFormData({
        title: task.title || '',
        description: task.description || '',
        urgency: task.urgency || 3,
        deadline: task.deadline ? new Date(task.deadline).toISOString().slice(0, 10) : '',
        status: task.status || 'todo',
        assignedTo: assignedUserId,
        assignees: assignedUserIds,
        subtasks: task.subtasks || [],
        projectId: task.projectId?._id || task.projectId || '',
        reviewerId: task.reviewerId?._id || task.reviewerId || '',
      });
    } else {
      setFormData({
        title: '',
        description: '',
        urgency: 3,
        deadline: '',
        status: 'todo',
        assignedTo: '',
        assignees: [],
        subtasks: [],
        projectId: '',
        reviewerId: '',
      });
    }
  }, [task, isOpen]);

  // Fetch full task details (including reviews) when modal opens
  useEffect(() => {
    const load = async () => {
      if (!task || !isOpen) return;
      try {
        const resp = await tasksAPI.getById(task._id);
        const full = resp.data || resp.data.task || resp.data;
        setReviews(full.reviews || []);
        setComments(full.comments || []);
      } catch (err) {
        console.error('Failed to load task details', err);
      }
    };
    load();
  }, [task, isOpen]);

  const isReviewer = (() => {
    if (!task || !user) return false;
    const rid = task.reviewerId;
    if (!rid) return false;
    if (typeof rid === 'object') return (rid._id === user._id || rid._id === user.id);
    return String(rid) === String(user._id || user.id);
  })();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addSubtask = () => {
    if (newSubtask.trim()) {
      setFormData(prev => ({
        ...prev,
        subtasks: [...prev.subtasks, { title: newSubtask, completed: false }],
      }));
      setNewSubtask('');
    }
  };

  const removeSubtask = (index) => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.filter((_, i) => i !== index),
    }));
  };

  const toggleAssignee = (userId) => {
    setFormData(prev => {
      const assignees = [...prev.assignees];
      const index = assignees.indexOf(userId);

      if (index > -1) {
        assignees.splice(index, 1);
      } else {
        assignees.push(userId);
      }

      return { ...prev, assignees };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.title.trim() || !formData.description.trim()) {
      alert('Title and description are required!');
      return;
    }

    setIsSubmitting(true);

    const taskData = {
      ...formData,
      urgency: Number(formData.urgency),
      assignees: formData.assignees && formData.assignees.length > 0 ? formData.assignees : [],
      projectId: formData.projectId || null,
    };

    // Remove assignedTo and assignee fields - we only use assignees now
    delete taskData.assignedTo;

    let result;
    if (task) {
      result = await updateTask(task._id, taskData);
    } else {
      result = await createTask(taskData);
    }

    setIsSubmitting(false);

    if (result.success) {
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSubtask();
    }
  };

  // Reviews & Comments helpers
  const addReview = async (status, feedback) => {
    if (!task) return;
    try {
      const body = { status, feedback };
      // call dedicated addReview endpoint
      const resp = await tasksAPI.addReview(task._id, body);
      const full = resp.data.task || resp.data;
      setReviews(full.reviews || []);
      if (typeof loadTasks === 'function') await loadTasks();
    } catch (err) {
      console.error('Failed to add review', err);
      alert('Failed to submit review');
    }
  };

  const approveTask = async () => {
    if (!task) return;
    try {
      await tasksAPI.approve(task._id, { feedback: feedbackText });
      // reload details
      const resp = await tasksAPI.getById(task._id);
      const full = resp.data || resp.data.task || resp.data;
      setReviews(full.reviews || []);
      if (typeof loadTasks === 'function') await loadTasks();
      setFeedbackText('');
    } catch (err) {
      console.error('Approve failed', err);
      alert('Failed to approve task');
    }
  };

  const requestChanges = async () => {
    if (!task) return;
    try {
      await tasksAPI.requestChanges(task._id, { feedback: feedbackText });
      const resp = await tasksAPI.getById(task._id);
      const full = resp.data || resp.data.task || resp.data;
      setReviews(full.reviews || []);
      if (typeof loadTasks === 'function') await loadTasks();
      setFeedbackText('');
    } catch (err) {
      console.error('Request changes failed', err);
      alert('Failed to request changes');
    }
  };

  const addComment = () => {
    if (!newComment.trim()) return;
    // TODO: Backend API call - POST /tasks/:id/comments
    const comment = {
      _id: `temp-${Date.now()}`,
      author: { _id: user?._id || 'u-temp', username: user?.username || 'You' },
      text: newComment.trim(),
      createdAt: new Date().toISOString(),
      isSystemMessage: false
    };
    setComments(prev => [comment, ...prev]);
    setNewComment('');
  };

  const formatTimeAgo = (dateString) => {
    // Simple time formatting - replace with date-fns in production
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{task ? 'View Full Task' : 'Create New Task'}</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        {/* <form onSubmit={handleSubmit} className="task-form">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter task title"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter task description"
              rows={4}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="urgency">Urgency</label>
              <select
                id="urgency"
                name="urgency"
                value={formData.urgency}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value={1}>1 - Minimal</option>
                <option value={2}>2 - Low</option>
                <option value={3}>3 - Medium</option>
                <option value={4}>4 - High</option>
                <option value={5}>5 - Critical</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="todo">To Do</option>
                <option value="in-progress">In Progress</option>
                <option value="review">Review</option>
                <option value="done">Done</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="deadline">Deadline</label>
              <input
                type="date"
                id="deadline"
                name="deadline"
                value={formData.deadline}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
            {projects.length > 0 && (
              <div className="form-group">
                <label htmlFor="projectId">Project</label>
                <select
                  id="projectId"
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="">No Project</option>
                  {projects.map(p => (
                    <option key={p._id} value={p._id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Assign To (Multiple)</label>
            <div className="assignees-selection">
              {users.map(u => (
                <div key={u._id} className="assignee-checkbox">
                  <input
                    type="checkbox"
                    id={`assignee-${u._id}`}
                    checked={formData.assignees.includes(u._id)}
                    onChange={() => toggleAssignee(u._id)}
                    disabled={isSubmitting}
                  />
                  <label htmlFor={`assignee-${u._id}`}>
                    {u.username}
                    {u.status && <span className={`status-badge ${u.status.toLowerCase().replace(' ', '-')}`}>{u.status}</span>}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {task && (
            <div className="form-group">
              <label>Subtasks</label>
              <div className="subtask-input-group">
                <input
                  type="text"
                  value={newSubtask}
                  onChange={(e) => setNewSubtask(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Add a subtask"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={addSubtask}
                  className="add-subtask-btn"
                  disabled={isSubmitting}
                >
                  <FiPlus size={20} />
                </button>
              </div>

              {formData.subtasks.length > 0 && (
                <div className="subtasks-list">
                  {formData.subtasks.map((subtask, index) => (
                    <div key={index} className="subtask-item subtask-item-with-actions">
                      <span className="subtask-title">{typeof subtask === 'string' ? subtask : subtask.title}</span>
                      <div className="subtask-actions">
                        {task && (
                          <SplitSubtaskButton
                            parentTask={task}
                            subtaskIndex={index}
                            subtask={subtask}
                            onSplit={(newTask) => {
                              // Remove from local form state after split
                              setFormData(prev => ({
                                ...prev,
                                subtasks: prev.subtasks.filter((_, i) => i !== index),
                              }));
                            }}
                            removeAfterSplit={false}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => removeSubtask(index)}
                          className="remove-subtask-btn"
                          disabled={isSubmitting}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-btn"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : task ? 'Update Task' : 'Create Task'}
            </button>
          </div>
        </form> */}
        <div className="reviews-comments-container">
          {/* Task Approval / Review Workflow Section */}
          <div className="reviews-section">
            <div className="section-header">
              <h3>📋 Task Review & Approval</h3>
              <span className="info-badge">Approval workflow for quality control</span>
            </div>

            {/* Review Status Summary */}
            <div className="review-status-summary">
              <div className="status-item approved">
                <span className="status-count">{reviews.filter(r => r.status === 'approved').length}</span>
                <span className="status-label">Passed</span>
              </div>
              <div className="status-item pending">
                <span className="status-count">{reviews.filter(r => r.status === 'changes-requested').length}</span>
                <span className="status-label">Need Changes</span>
              </div>
              <div className="status-item rejected">
                <span className="status-count">{reviews.filter(r => r.status === 'rejected').length}</span>
                <span className="status-label">Failed</span>
              </div>
            </div>

            {/* Reviews List */}
            <div className="reviews-list">
              {reviews.length === 0 ? (
                <div className="empty-state">No reviews yet. Task pending review.</div>
              ) : (
                reviews.map((review, idx) => (
                  <div key={idx} className={`review-item status-${review.status}`}>
                    <div className="review-header">
                      <div className="reviewer-info">
                        <div className="reviewer-avatar">{(review.reviewerId?.username || review.reviewerName || 'R').charAt(0)}</div>
                        <div>
                          <div className="reviewer-name">{review.reviewerId?.username || review.reviewerName || 'Reviewer'}</div>
                          <div className="review-time">{formatTimeAgo(review.createdAt)}</div>
                        </div>
                      </div>
                      <span className={`review-status-badge ${review.status}`}>
                        {review.status === 'approved' && '✓ Passed'}
                        {review.status === 'changes-requested' && '⚠ Need Changes'}
                        {review.status === 'rejected' && '✗ Failed'}
                      </span>
                    </div>
                    <div className="review-feedback">{review.feedback}</div>
                    {/* TODO: Show attachments if any */}
                  </div>
                ))
              )}
            </div>

            {/* Add Review Form - Only for managers/reviewers */}
            {(isReviewer || user?.role === 'manager' || user?.role === 'admin') ? (
              <div className="add-review-form">
                <textarea
                  placeholder="Add your review feedback..."
                  className="review-textarea"
                  rows={3}
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                />
                <div className="review-actions-form">
                  <button className="review-btn approve" onClick={() => addReview('approved', feedbackText)}>✓ Passed</button>
                  <button className="review-btn request-changes" onClick={() => addReview('changes-requested', feedbackText)}>⚠ Need Changes</button>
                  <button className="review-btn reject" onClick={() => addReview('rejected', feedbackText)} style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#fca5a5' }}>✗ Failed</button>
                </div>
              </div>
            ) : (
              <div className="info-message">Only assigned reviewer or managers can add reviews</div>
            )}
          </div>

          {/* Activity Discussion / Comments Section */}
          <div className="comments-section">
            <div className="section-header">
              <h3>💬 Activity & Discussion</h3>
              <span className="comment-count">{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</span>
            </div>

            {/* Add Comment Input */}
            <div className="comment-input-wrapper">
              <div className="comment-avatar">{user?.username?.charAt(0) || 'U'}</div>
              <div className="comment-input-group">
                <input
                  type="text"
                  placeholder="Add a comment or update..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addComment()}
                  className="comment-input"
                />
                <button onClick={addComment} className="comment-submit-btn" disabled={!newComment.trim()}>
                  Send
                </button>
              </div>
            </div>

            {/* Comments List / Activity Feed */}
            <div className="comments-list">
              {comments.length === 0 ? (
                <div className="empty-state">No comments yet. Start the discussion!</div>
              ) : (
                comments.slice().reverse().map((comment) => (
                  <div key={comment._id} className={`comment-item ${comment.isSystemMessage ? 'system-message' : ''}`}>
                    {!comment.isSystemMessage && (
                      <div className="comment-avatar">{comment.author.username.charAt(0)}</div>
                    )}
                    <div className="comment-content">
                      <div className="comment-header">
                        <span className="comment-author">{comment.author.username}</span>
                        <span className="comment-time">{formatTimeAgo(comment.createdAt)}</span>
                      </div>
                      <div className="comment-text">{comment.text}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;
